import type { MediaFile } from '@/lib/services/media/media.types'
import { ProductAddOnStorefrontVisibility } from '@/lib/enums/ProductAddOnStorefrontVisibility'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { ProductCatalogType } from '@/lib/product-catalog-type'
import { getProductRole, type ProductRole } from '@/lib/product-role'
import { normalizeRichTextHtml, plainTextToRichTextHtml } from '@/lib/rich-text'
import {
  mergeMissingProductRichTextFields,
  mergeMissingRichTextFields,
} from '@/lib/rich-text-merge'
import type { Collection } from '@/lib/services/collections/collections.types'
import type {
  AddOnSetListItem,
  LinkedAddOnProduct,
} from '@/lib/services/add-on-sets/addOnSets.types'
import { normalizeOccasionIds } from '@/lib/services/occasions/occasions.utils'
import {
  defaultSubscriptionDisclaimer,
  defaultSubscriptionDisclaimerHtml,
  mapProductVisibilityToAddOnVisibility,
} from '@/lib/services/products/products.role-transition'
import {
  defaultSubscriptionFrequencyOptions,
  subscriptionFrequencyOptions,
} from '@/lib/subscription-frequency'
import {
  getErrorMessage,
  normalizeCurrencyValue,
  readProperty,
  type NumericInput,
} from '@/lib/utils'
import type {
  Product,
  ProductAddOnSet,
  ProductAddOnStorefrontVisibility as ProductAddOnStorefrontVisibilityValue,
  ProductCatalogType as ProductCatalogTypeValue,
  ProductSearchTagDraft,
  ProductStatus,
  ProductStorefrontVisibility as ProductStorefrontVisibilityValue,
  SubscriptionFrequency,
  Variant,
  VariantFulfillmentType,
} from './products.types'

type IdList = readonly (number | string | null | undefined)[] | null | undefined

type ProductFormVariantField =
  | 'id'
  | 'name'
  | 'description'
  | 'description_html'
  | 'recipe'
  | 'recipe_html'
  | 'sku'
  | 'barcodes'
  | 'fulfillment_types'
  | 'media'
  | 'price'
  | 'cost'
  | 'use_inventory'
  | 'on_hand'
  | 'add_on_max_quantity'
  | 'is_default'

/**
 * A variant as the product form edits it: a saved variant (with the rest of `VariantResource`), a
 * new one with a `new-` id, or a copy.
 */
export type ProductFormVariant = Partial<Omit<Variant, ProductFormVariantField>> & {
  id: number | `new-${string}`
  name: string
  description: string | null
  description_html?: string | null
  recipe: string | null
  recipe_html?: string | null
  sku: string | null
  barcodes: string[]
  fulfillment_types: VariantFulfillmentType[]
  media?: MediaFile | null
  /** Saved prices are decimal strings, new variants start with numbers, and inputs hold typed text. */
  price: number | string
  /** Left out for users who may not see costs. */
  cost?: number | string
  use_inventory: boolean
  on_hand: number | string | null
  add_on_max_quantity: number | string | null
  is_default: boolean
}

type ProductFormField =
  | 'name'
  | 'description'
  | 'description_html'
  | 'product_catalog_type'
  | 'subscription_frequency_options'
  | 'subscription_disclaimer'
  | 'subscription_disclaimer_html'
  | 'is_designers_choice'
  | 'is_add_on'
  | 'is_add_on_only'
  | 'status'
  | 'storefront_visibility'
  | 'add_on_storefront_visibility'
  | 'page_title'
  | 'meta_description'
  | 'collections'
  | 'media'
  | 'occasion_ids'
  | 'search_tag_ids'
  | 'add_on_sets'
  | 'variants'

/** Detail responses and the picker may also load the set's target collections and count. */
export type ProductFormAddOnSet = ProductAddOnSet &
  Partial<Pick<AddOnSetListItem, 'applies_to_collections' | 'add_ons_count'>>

/** The product form's values: a saved product's fields, or `emptyProduct`'s, ready to edit. */
export type ProductFormValues = Partial<Omit<Product, ProductFormField>> & {
  name: string
  description: string | null
  description_html: string | null
  product_role: ProductRole
  product_catalog_type: ProductCatalogTypeValue
  subscription_frequency_options: SubscriptionFrequency[]
  subscription_disclaimer: string
  subscription_disclaimer_html: string | null
  is_designers_choice: boolean
  is_add_on: boolean
  is_add_on_only: boolean
  status: ProductStatus
  storefront_visibility: ProductStorefrontVisibilityValue
  add_on_storefront_visibility: ProductAddOnStorefrontVisibilityValue
  page_title: string | null
  meta_description: string | null
  /** Manual collections; smart collections pick their own products. */
  collections: Collection[]
  media: MediaFile[]
  occasion_ids: number[]
  search_tag_ids: number[]
  add_on_sets: ProductFormAddOnSet[]
  variants: ProductFormVariant[]
}

/** Fields edited by the form, without the optional back-reference to the whole product. */
export type ProductEditorVariant = Omit<ProductFormVariant, 'product'>

/** A selected collection keeps its own fields; its optional loaded relations are not form fields. */
export type ProductEditorCollection = Omit<Collection, 'products' | 'add_on_sets' | 'organization'>

type ProductEditorAddOn = Omit<LinkedAddOnProduct, 'variants' | 'defaultVariant'> & {
  defaultVariant: Omit<Variant, 'product'> | null
}

export type ProductEditorAddOnSet = Omit<
  ProductFormAddOnSet,
  'add_ons' | 'applies_to_collections'
> & {
  add_ons?: ProductEditorAddOn[]
  applies_to_collections?: ProductEditorCollection[]
}

/**
 * TanStack Form's editable view of the same values. Optional loaded API relations are not editable
 * paths; excluding them from the view avoids recursively expanding Product/Variant/Collection keys.
 * The actual objects are preserved, and this view remains assignable to ProductFormValues.
 */
export type ProductEditorValues = Omit<
  ProductFormValues,
  | 'organization'
  | 'category'
  | 'defaultVariant'
  | 'available_add_on_products'
  | 'variants'
  | 'collections'
  | 'add_on_sets'
> & {
  variants: ProductEditorVariant[]
  collections: ProductEditorCollection[]
  add_on_sets: ProductEditorAddOnSet[]
}

/**
 * What the form normalizes: a saved product, its form values, or a saved product whose response
 * left out variants or rich text, filled in from the submitted values.
 */
export type ProductFormSource =
  | (Omit<Product, 'variants'> & { variants?: ProductFormVariant[] })
  | ProductFormValues

/** A variant as `normalizeProductPayload` sends it. */
export type ProductPayloadVariant = Omit<
  ProductFormVariant,
  'price' | 'cost' | 'add_on_max_quantity'
> & {
  price: number
  cost: number
  add_on_max_quantity: number | null
}

/**
 * What `normalizeProductPayload` sends to the create and update endpoints: the form values with the
 * fields each product role doesn't use cleared. Laravel validates this editable draft when the form
 * submits it; accepted `CreateProductInput` / `UpdateProductInput` payloads are stricter.
 */
export type ProductPayload = Omit<
  ProductFormValues,
  | 'product_role'
  | 'category'
  | 'subscription_disclaimer'
  | 'subscription_disclaimer_html'
  | 'variants'
> & {
  subscription_disclaimer: string | null
  subscription_disclaimer_html: string | null
  collections_were_edited: boolean
  variants: ProductPayloadVariant[]
}

const emptyProduct: ProductFormValues = {
  name: '',
  description: '',
  description_html: null,
  product_role: 'product',
  product_catalog_type: ProductCatalogType.Standard,
  subscription_frequency_options: defaultSubscriptionFrequencyOptions,
  subscription_disclaimer: defaultSubscriptionDisclaimer,
  subscription_disclaimer_html: defaultSubscriptionDisclaimerHtml,
  is_designers_choice: false,
  is_add_on: false,
  is_add_on_only: false,
  status: 'active',
  storefront_visibility: ProductStorefrontVisibility.PublishedCollection.Value,
  add_on_storefront_visibility: ProductAddOnStorefrontVisibility.Hidden.Value,
  page_title: '',
  meta_description: '',
  collections: [],
  media: [],
  occasion_ids: [],
  search_tag_ids: [],
  add_on_sets: [],
  variants: [],
}

function createEmptyVariant({
  isDefault = false,
  name = '',
  price = 0,
}: { isDefault?: boolean; name?: string; price?: NumericInput } = {}): ProductFormVariant {
  return {
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    name,
    description: '',
    description_html: null,
    recipe: '',
    recipe_html: null,
    sku: '',
    barcodes: [],
    fulfillment_types: ['pickup', 'delivery'],
    media: null,
    price: normalizeCurrencyValue(price),
    cost: 0,
    use_inventory: false,
    on_hand: null,
    add_on_max_quantity: null,
    is_default: isDefault,
  }
}

function normalizeVariantFulfillmentTypes(
  value: readonly string[] | null | undefined
): VariantFulfillmentType[] {
  const selected = new Set(Array.isArray(value) ? value : [])
  const supported = (['pickup', 'delivery'] satisfies VariantFulfillmentType[]).filter((type) =>
    selected.has(type)
  )

  return supported.length > 0 ? supported : ['pickup', 'delivery']
}

function createStandardDeluxePremiumVariants(standardPrice: NumericInput): ProductFormVariant[] {
  const normalizedStandardPrice = normalizeCurrencyValue(standardPrice)

  return [
    createEmptyVariant({
      isDefault: true,
      name: 'Standard',
      price: normalizedStandardPrice,
    }),
    createEmptyVariant({
      name: 'Deluxe',
      price: normalizeCurrencyValue(normalizedStandardPrice * 1.5),
    }),
    createEmptyVariant({
      name: 'Premium',
      price: normalizeCurrencyValue(normalizedStandardPrice * 2),
    }),
  ]
}

function createCopiedVariant(
  sourceVariant: ProductFormVariant | null | undefined,
  { isDefault = false }: { isDefault?: boolean } = {}
): ProductFormVariant {
  return {
    ...createEmptyVariant({ isDefault }),
    description: String(sourceVariant?.description ?? ''),
    description_html: sourceVariant?.description_html ?? null,
    recipe: String(sourceVariant?.recipe ?? ''),
    recipe_html: sourceVariant?.recipe_html ?? null,
    sku: String(sourceVariant?.sku ?? ''),
    // A barcode belongs to one item, so a copy starts without any.
    add_on_max_quantity: sourceVariant?.add_on_max_quantity ?? null,
    fulfillment_types: normalizeVariantFulfillmentTypes(sourceVariant?.fulfillment_types),
    media: sourceVariant?.media ? { ...sourceVariant.media } : null,
    name: '',
    price: normalizeCurrencyValue(sourceVariant?.price ?? 0),
    cost: normalizeCurrencyValue(sourceVariant?.cost ?? 0),
    use_inventory: Boolean(sourceVariant?.use_inventory),
    // Copied variants should keep the tracking toggle, but not inherit live stock counts.
    on_hand: null,
    is_default: isDefault,
  }
}

function isBlankVariant(variant: ProductFormVariant | null | undefined) {
  const fulfillmentTypes = Array.isArray(variant?.fulfillment_types)
    ? [...variant.fulfillment_types].sort()
    : []

  return (
    !String(variant?.name ?? '').trim() &&
    !String(variant?.description ?? '').trim() &&
    !String(variant?.description_html ?? '').trim() &&
    !String(variant?.recipe ?? '').trim() &&
    !String(variant?.recipe_html ?? '').trim() &&
    !String(variant?.sku ?? '').trim() &&
    !variant?.barcodes?.length &&
    !variant?.media &&
    normalizeCurrencyValue(variant?.price ?? 0) === 0 &&
    normalizeCurrencyValue(variant?.cost ?? 0) === 0 &&
    (variant?.add_on_max_quantity === null ||
      variant?.add_on_max_quantity === '' ||
      typeof variant?.add_on_max_quantity === 'undefined') &&
    !variant?.use_inventory &&
    (variant?.on_hand === null ||
      variant?.on_hand === '' ||
      typeof variant?.on_hand === 'undefined') &&
    variant?.is_default &&
    fulfillmentTypes.length === 2 &&
    fulfillmentTypes[0] === 'delivery' &&
    fulfillmentTypes[1] === 'pickup'
  )
}

function shouldShowPresetVariantAction(variants: ProductFormVariant[] | null | undefined) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return true
  }

  return variants.length === 1 && isBlankVariant(variants[0])
}

function isAddOnProductRole(productRole: ProductRole | null | undefined) {
  return productRole === 'standalone_add_on' || productRole === 'add_on_only'
}

function isSubscriptionPackageRole(productRole: ProductRole | null | undefined) {
  return productRole === 'subscription_package'
}

function normalizeSubscriptionFrequencyValues(
  value: readonly string[] | null | undefined
): SubscriptionFrequency[] {
  const selectedValues = new Set(Array.isArray(value) ? value.map(String) : [])

  return subscriptionFrequencyOptions
    .map((option) => option.id)
    .filter((frequency) => selectedValues.has(frequency))
}

function normalizeSubscriptionDisclaimer(value: string | null | undefined) {
  const disclaimer = String(value ?? '').trim()

  return disclaimer || defaultSubscriptionDisclaimer
}

function normalizeSubscriptionDisclaimerHtml(
  value: string | null | undefined,
  fallbackText: string | null | undefined
) {
  return (
    normalizeRichTextHtml(value) ||
    plainTextToRichTextHtml(normalizeSubscriptionDisclaimer(fallbackText))
  )
}

function isManualCollection(
  collection: Pick<Collection, 'collection_membership_type'> | null | undefined
) {
  return collection?.collection_membership_type !== 'smart'
}

function getMediaId(media: { id?: number | string | null } | null | undefined) {
  const mediaId = media?.id

  return mediaId === null || typeof mediaId === 'undefined' || mediaId === ''
    ? null
    : String(mediaId)
}

function getPreferredActiveVariantId(variants: ProductFormVariant[] | null | undefined) {
  const preferredVariant =
    variants?.find((variant) => Boolean(variant?.is_default)) ?? variants?.[0] ?? null

  return preferredVariant?.id ? String(preferredVariant.id) : null
}

function getVariantLabel(variant: { name?: string | null } | null | undefined, index: number) {
  const label = String(variant?.name ?? '').trim()

  return label || `Variant ${index + 1}`
}

function normalizeServerErrorPath(path: string | null | undefined) {
  return String(path ?? '').replace(/\.(\d+)(?=\.|$)/g, '[$1]')
}

function getFirstServerError(errors: unknown) {
  if (typeof errors !== 'object' || errors === null) {
    return null
  }

  const [path] = Object.keys(errors)

  if (typeof path === 'undefined') {
    return null
  }

  const messages = readProperty(errors, path)
  const message: unknown = Array.isArray(messages) ? messages[0] : messages

  return {
    path,
    message: typeof message === 'string' ? message : getErrorMessage(message),
  }
}

/** Laravel names errors `variants.0.price`; the form names them `variants[0].price`. */
function normalizeProductServerErrors<TMessages>(
  errors: Record<string, TMessages> | null | undefined
): Record<string, TMessages>
function normalizeProductServerErrors(errors: unknown): Record<string, unknown>
function normalizeProductServerErrors(errors: unknown): Record<string, unknown> {
  return Object.entries(errors ?? {}).reduce<Record<string, unknown>>(
    (normalizedErrors, [path, messages]) => {
      normalizedErrors[normalizeServerErrorPath(path)] = messages

      return normalizedErrors
    },
    {}
  )
}

function getFirstInvalidVariantIndex(errors: Record<string, unknown> | null | undefined) {
  const firstVariantError = Object.keys(errors ?? {})
    .map(normalizeServerErrorPath)
    .find((path) => /^variants\[\d+\]\./.test(path))

  if (!firstVariantError) {
    return null
  }

  const variantIndex = Number(firstVariantError.match(/^variants\[(\d+)\]\./)?.[1])

  return Number.isInteger(variantIndex) ? variantIndex : null
}

const variantValidationSummariesByField: Record<string, string> = {
  name: 'needs a name.',
  price: 'needs a price.',
  cost: 'needs a valid cost.',
  fulfillment_types: 'needs fulfillment options.',
  'media.id': 'needs a valid photo selection.',
  on_hand: 'needs a quantity on hand.',
  use_inventory: 'needs a valid inventory setting.',
  weight: 'needs a valid weight.',
}

/** The submitted values the validation toast reads. */
interface ValidationToastProduct {
  variants?: { name?: string | null }[]
}

function getVariantValidationToastDescription({
  path,
  message,
  product,
}: {
  path: string
  message: string | undefined
  product: ValidationToastProduct | null | undefined
}) {
  const normalizedPath = normalizeServerErrorPath(path)
  const match = normalizedPath.match(/^variants\[(\d+)\]\.(.+)$/)

  if (!match) {
    return null
  }

  const variantIndex = Number(match[1])
  const fieldName = match[2] ?? ''
  const variantLabel = getVariantLabel(product?.variants?.[variantIndex], variantIndex)
  const summary = variantValidationSummariesByField[fieldName]

  return summary ? `${variantLabel} ${summary}` : `${variantLabel}: ${message}`
}

function getProductValidationToastDescription(
  error: unknown,
  product: ValidationToastProduct | null | undefined
) {
  const firstServerError = getFirstServerError(
    readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')
  )

  if (!firstServerError) {
    return getErrorMessage(error)
  }

  return (
    getVariantValidationToastDescription({
      path: firstServerError.path,
      message: firstServerError.message,
      product,
    }) ||
    firstServerError.message ||
    getErrorMessage(error)
  )
}

function getVariantWorkspaceSignature(variants: ProductFormVariant[] | null | undefined) {
  return (variants ?? [])
    .map((variant, index) =>
      [
        String(variant?.id ?? index),
        getVariantLabel(variant, index),
        variant?.is_default ? '1' : '0',
        isBlankVariant(variant) ? '0' : '1',
      ].join('::')
    )
    .join('|||')
}

/** The plain text and rich text description fields a product or variant has. */
interface DescriptionSource {
  description?: string | null
  description_html?: string | null
}

function getComparableDescriptionHtml({
  description,
  descriptionHtml,
}: {
  description: string | null | undefined
  descriptionHtml: string | null | undefined
}) {
  const normalizedRichTextHtml = normalizeRichTextHtml(descriptionHtml)

  if (normalizedRichTextHtml) {
    return normalizedRichTextHtml
  }

  return plainTextToRichTextHtml(String(description ?? '').trim())
}

function usesDefaultProductDescription(
  variant: DescriptionSource | null | undefined,
  product: DescriptionSource | null | undefined
) {
  const variantDescriptionHtml = getComparableDescriptionHtml({
    description: variant?.description,
    descriptionHtml: variant?.description_html,
  })

  if (!variantDescriptionHtml) {
    return true
  }

  return (
    variantDescriptionHtml ===
    getComparableDescriptionHtml({
      description: product?.description,
      descriptionHtml: product?.description_html,
    })
  )
}

/** A variant that repeats the product description sends none, so it follows the product's. */
function collapseDefaultVariantDescription(
  variant: ProductFormVariant,
  product: DescriptionSource | null | undefined
): ProductFormVariant {
  if (!usesDefaultProductDescription(variant, product)) {
    return variant
  }

  return {
    ...variant,
    description: '',
    description_html: null,
  }
}

// Normalization spreads saved identity through unchanged.
function normalizeProductFormValues(
  product: ProductFormSource & Pick<Product, 'id'>
): ProductFormValues & Pick<Product, 'id'>
function normalizeProductFormValues(product: ProductFormSource): ProductFormValues
function normalizeProductFormValues(product: ProductFormSource): ProductFormValues {
  const productRole = getProductRole(product)
  const isSubscriptionPackage = isSubscriptionPackageRole(productRole)
  const isAddOnOnly = productRole === 'add_on_only'
  const productVisibility =
    product?.storefront_visibility ?? ProductStorefrontVisibility.PublishedCollection.Value
  const addOnVisibility =
    product?.add_on_storefront_visibility ??
    (!isAddOnProductRole(productRole)
      ? ProductAddOnStorefrontVisibility.Hidden.Value
      : mapProductVisibilityToAddOnVisibility(productVisibility))
  const subscriptionFrequencyValues = normalizeSubscriptionFrequencyValues(
    product?.subscription_frequency_options
  )
  const subscriptionDisclaimer = normalizeSubscriptionDisclaimer(product?.subscription_disclaimer)

  return {
    ...emptyProduct,
    ...product,
    product_role: productRole,
    product_catalog_type: isSubscriptionPackage
      ? ProductCatalogType.SubscriptionPackage
      : ProductCatalogType.Standard,
    subscription_frequency_options:
      isSubscriptionPackage && subscriptionFrequencyValues.length > 0
        ? subscriptionFrequencyValues
        : defaultSubscriptionFrequencyOptions,
    subscription_disclaimer: isSubscriptionPackage
      ? subscriptionDisclaimer
      : defaultSubscriptionDisclaimer,
    subscription_disclaimer_html: isSubscriptionPackage
      ? normalizeSubscriptionDisclaimerHtml(
          product?.subscription_disclaimer_html,
          subscriptionDisclaimer
        )
      : defaultSubscriptionDisclaimerHtml,
    is_designers_choice: Boolean(product?.is_designers_choice),
    is_add_on: isSubscriptionPackage
      ? false
      : Boolean(product?.is_add_on || product?.is_add_on_only),
    is_add_on_only: isSubscriptionPackage ? false : Boolean(product?.is_add_on_only),
    storefront_visibility: productVisibility,
    add_on_storefront_visibility: addOnVisibility,
    collections: Array.isArray(product?.collections)
      ? product.collections.filter(isManualCollection)
      : [],
    occasion_ids: isAddOnOnly
      ? []
      : normalizeOccasionIds({
          occasion_ids: product?.occasion_ids,
          occasions: product?.occasions,
        }),
    search_tag_ids: normalizeSearchTagIds(product),
    add_on_sets:
      isSubscriptionPackage || !isAddOnProductRole(productRole)
        ? []
        : Array.isArray(product?.add_on_sets)
          ? product.add_on_sets
          : [],
    variants: (product?.variants ?? []).map((variant) => ({
      ...variant,
      add_on_max_quantity: variant?.add_on_max_quantity ?? null,
      fulfillment_types: normalizeVariantFulfillmentTypes(variant?.fulfillment_types),
    })),
  }
}

function normalizeProductPayload(
  value: ProductFormValues,
  { collectionsWereEdited = false }: { collectionsWereEdited?: boolean } = {}
): ProductPayload {
  const productRole = getProductRole(value)
  const isSubscriptionPackage = isSubscriptionPackageRole(productRole)
  const isAddOn = isAddOnProductRole(productRole)
  const isAddOnOnly = productRole === 'add_on_only'
  const { product_role: _productRole, category: _category, ...payloadValue } = value
  // Product values copied from older data can carry `applies_to_collections`, which the product
  // requests prohibit; it isn't part of the form's type, so it is dropped by name.
  Reflect.deleteProperty(payloadValue, 'applies_to_collections')
  const storefrontVisibility = isAddOnOnly
    ? ProductStorefrontVisibility.Hidden.Value
    : (value?.storefront_visibility ?? ProductStorefrontVisibility.PublishedCollection.Value)
  const addOnStorefrontVisibility = isAddOn
    ? (value?.add_on_storefront_visibility ??
      mapProductVisibilityToAddOnVisibility(storefrontVisibility))
    : ProductAddOnStorefrontVisibility.Hidden.Value
  const subscriptionDisclaimer = normalizeSubscriptionDisclaimer(value?.subscription_disclaimer)

  return {
    ...payloadValue,
    product_catalog_type: isSubscriptionPackage
      ? ProductCatalogType.SubscriptionPackage
      : ProductCatalogType.Standard,
    subscription_frequency_options: isSubscriptionPackage
      ? normalizeSubscriptionFrequencyValues(value?.subscription_frequency_options)
      : [],
    subscription_disclaimer: isSubscriptionPackage ? subscriptionDisclaimer : null,
    subscription_disclaimer_html: isSubscriptionPackage
      ? normalizeSubscriptionDisclaimerHtml(
          value?.subscription_disclaimer_html,
          subscriptionDisclaimer
        )
      : null,
    storefront_visibility: storefrontVisibility,
    add_on_storefront_visibility: addOnStorefrontVisibility,
    collections: isAddOnOnly ? [] : (value?.collections ?? []),
    collections_were_edited: isAddOnOnly ? true : collectionsWereEdited,
    occasion_ids: isAddOnOnly ? [] : (value?.occasion_ids ?? []),
    search_tag_ids: isAddOnOnly ? [] : (value?.search_tag_ids ?? []),
    add_on_sets: isAddOn ? (value?.add_on_sets ?? []) : [],
    is_designers_choice: isAddOnOnly ? false : Boolean(value?.is_designers_choice),
    is_add_on: isAddOn,
    is_add_on_only: isAddOnOnly,
    variants: (value?.variants ?? []).map((rawVariant) => {
      const variant = collapseDefaultVariantDescription(rawVariant, value)
      const hasAddOnMaxQuantity = String(rawVariant?.add_on_max_quantity ?? '').trim() !== ''
      const addOnMaxQuantity = Number(rawVariant?.add_on_max_quantity)

      return {
        ...variant,
        ...(isSubscriptionPackage
          ? {
              add_on_max_quantity: null,
              barcodes: [],
              cost: 0,
              on_hand: null,
              recipe: '',
              recipe_html: null,
              sku: '',
              use_inventory: false,
            }
          : {}),
        add_on_max_quantity:
          isAddOn &&
          !isSubscriptionPackage &&
          hasAddOnMaxQuantity &&
          Number.isFinite(addOnMaxQuantity)
            ? addOnMaxQuantity
            : null,
        price: normalizeCurrencyValue(variant?.price),
        cost: isSubscriptionPackage ? 0 : normalizeCurrencyValue(variant?.cost),
      }
    }),
  }
}

function buildProductSearchTagRecommendationDraft(value: ProductFormValues): ProductSearchTagDraft {
  const usesDesignerChoice = Boolean(value?.is_designers_choice)
  const isSubscriptionPackage = isSubscriptionPackageRole(getProductRole(value))

  return {
    name: value?.name ?? '',
    source_key: value?.source_key ?? '',
    is_designers_choice: usesDesignerChoice,
    description: value?.description ?? '',
    description_html: value?.description_html ?? null,
    page_title: value?.page_title ?? '',
    meta_description: value?.meta_description ?? '',
    category: value?.category ? { name: value.category.name ?? '' } : null,
    collections: (value?.collections ?? []).map((collection) => ({
      name: collection?.name ?? '',
    })),
    variants: (value?.variants ?? []).map((variant) => ({
      name: variant?.name ?? '',
      sku: isSubscriptionPackage ? '' : (variant?.sku ?? ''),
      description: variant?.description ?? '',
      description_html: variant?.description_html ?? null,
      recipe: usesDesignerChoice || isSubscriptionPackage ? '' : (variant?.recipe ?? ''),
      recipe_html:
        usesDesignerChoice || isSubscriptionPackage ? null : (variant?.recipe_html ?? null),
    })),
  }
}

function normalizeTagIds(value: IdList) {
  return (Array.isArray(value) ? value : [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
}

function mergeUniqueIds(currentValue: IdList, additionalValue: IdList) {
  const seenIds = new Set<number>()

  return [...normalizeTagIds(currentValue), ...normalizeTagIds(additionalValue)].filter((id) => {
    if (seenIds.has(id)) {
      return false
    }

    seenIds.add(id)
    return true
  })
}

/** Collection fields that name its occasions and seasons; forms hold ids, responses tags. */
interface CollectionTagSource {
  occasion_ids?: IdList
  occasions?: ({ id?: number | null } | null | undefined)[] | null
  season_ids?: IdList
  seasons?: ({ id?: number | null } | null | undefined)[] | null
}

function normalizeCollectionOccasionIds(collection: CollectionTagSource | null | undefined) {
  const directOccasionIds = normalizeTagIds(collection?.occasion_ids)

  if (directOccasionIds.length > 0) {
    return directOccasionIds
  }

  return normalizeTagIds((collection?.occasions ?? []).map((occasion) => occasion?.id))
}

function normalizeCollectionsOccasionIds(
  collections: (CollectionTagSource | null | undefined)[] | null | undefined
) {
  return mergeUniqueIds(
    [],
    (Array.isArray(collections) ? collections : []).flatMap(normalizeCollectionOccasionIds)
  )
}

function normalizeCollectionSeasonIds(collection: CollectionTagSource | null | undefined) {
  const directSeasonIds = normalizeTagIds(collection?.season_ids)

  if (directSeasonIds.length > 0) {
    return directSeasonIds
  }

  return normalizeTagIds((collection?.seasons ?? []).map((season) => season?.id))
}

function normalizeCollectionsSeasonIds(
  collections: (CollectionTagSource | null | undefined)[] | null | undefined
) {
  return mergeUniqueIds(
    [],
    (Array.isArray(collections) ? collections : []).flatMap(normalizeCollectionSeasonIds)
  )
}

function areSameIdList(firstValue: IdList, secondValue: IdList) {
  const firstIds = normalizeTagIds(firstValue)
  const secondIds = normalizeTagIds(secondValue)

  return (
    firstIds.length === secondIds.length && firstIds.every((id, index) => id === secondIds[index])
  )
}

function normalizeSearchTagIds(product: { search_tag_ids?: IdList } | null | undefined) {
  return normalizeTagIds(product?.search_tag_ids)
}

/**
 * After a save, keeps the submitted variants and rich text the response left out, so resetting the
 * form to the response doesn't drop them.
 */
function mergeProductResetPayload(
  updatedProduct: Product,
  submittedProduct: ProductFormValues
): Omit<Product, 'variants'> & { variants?: ProductFormVariant[] }
function mergeProductResetPayload(
  updatedProduct: Product | null | undefined,
  submittedProduct: ProductFormValues
): ProductFormSource
function mergeProductResetPayload(
  updatedProduct: Product | null | undefined,
  submittedProduct: ProductFormValues
): ProductFormSource {
  if (!updatedProduct || typeof updatedProduct !== 'object' || Array.isArray(updatedProduct)) {
    return submittedProduct
  }

  const mergedProduct: Omit<Product, 'variants'> & { variants?: ProductFormVariant[] } =
    mergeMissingProductRichTextFields(updatedProduct, submittedProduct)
  const submittedVariants = Array.isArray(submittedProduct?.variants)
    ? submittedProduct.variants
    : []

  if (!Array.isArray(mergedProduct.variants)) {
    if (typeof mergedProduct.variants === 'undefined' && submittedVariants.length > 0) {
      mergedProduct.variants = submittedVariants
    }

    return mergedProduct
  }

  const submittedVariantsById = new Map(
    submittedVariants.map((variant, index): [string, ProductFormVariant] => [
      String(variant?.id ?? index),
      variant,
    ])
  )

  mergedProduct.variants = mergedProduct.variants.map((variant, index) => {
    const fallbackVariant =
      submittedVariantsById.get(String(variant?.id ?? index)) ?? submittedVariants[index]

    return mergeMissingRichTextFields(variant, fallbackVariant)
  })

  return mergedProduct
}

function getProductRoleLabel(
  productRole: ProductRole | null | undefined,
  { lowercase = false }: { lowercase?: boolean } = {}
) {
  const label = isSubscriptionPackageRole(productRole)
    ? 'Subscription package'
    : isAddOnProductRole(productRole)
      ? 'Add-on'
      : 'Product'

  return lowercase ? label.toLowerCase() : label
}

export {
  areSameIdList,
  buildProductSearchTagRecommendationDraft,
  createCopiedVariant,
  createEmptyVariant,
  createStandardDeluxePremiumVariants,
  emptyProduct,
  getFirstInvalidVariantIndex,
  getMediaId,
  getPreferredActiveVariantId,
  getProductRoleLabel,
  getProductValidationToastDescription,
  getVariantLabel,
  getVariantWorkspaceSignature,
  isAddOnProductRole,
  isBlankVariant,
  isSubscriptionPackageRole,
  mergeProductResetPayload,
  mergeUniqueIds,
  normalizeCollectionsOccasionIds,
  normalizeCollectionsSeasonIds,
  normalizeProductFormValues,
  normalizeProductPayload,
  normalizeProductServerErrors,
  normalizeVariantFulfillmentTypes,
  normalizeSubscriptionDisclaimer,
  normalizeSubscriptionFrequencyValues,
  normalizeTagIds,
  shouldShowPresetVariantAction,
}
